import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectAPI, reportAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { saveAs } from 'file-saver';
import { 
  FileText, 
  Share2, 
  Download,
  Loader2, 
  Building2,
  DollarSign,
  TrendingUp,
  Calendar,
  MapPin,
  Eye,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  Link as LinkIcon,
  Clock,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import ReportPreviewModal from '../../components/ReportPreviewModal';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return 'Not set';
  return new Date(date).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

const ReportsPage = () => {
  const [projects, setProjects] = useState([]);
  const [sharedReports, setSharedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewProject, setPreviewProject] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, sharedRes] = await Promise.all([
        projectAPI.getAll(),
        reportAPI.getMyShared()
      ]);
      setProjects(projectsRes.data || []);
      setSharedReports(sharedRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewReport = async (project) => {
    setLoadingPreview(true);
    setPreviewProject(project);
    
    try {
      const response = await reportAPI.getPreview(project.id);
      setPreviewData(response.data);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to load report preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownloadReport = async (project) => {
    setDownloading(project.id);
    
    try {
      toast.info('Generating PDF...');
      const response = await reportAPI.downloadPDF(project.id);
      
      if (!response.data || response.data.size === 0) {
        throw new Error('Empty PDF response');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `DealVault_${project.name.replace(/[^a-zA-Z0-9]/g, '_')}_Report.pdf`;
      
      saveAs(blob, filename);
      
      toast.success(`Report saved as "${filename}"!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Try the Share option instead.');
    } finally {
      setDownloading(null);
    }
  };

  const handleGenerateShareableLink = async (project) => {
    setGenerating(project.id);
    
    try {
      const response = await reportAPI.generateShareable(project.id);
      
      if (response.data.success) {
        // Copy to clipboard automatically
        await navigator.clipboard.writeText(response.data.share_url);
        setCopiedId(project.id);
        
        toast.success(
          <div>
            <p className="font-semibold">Shareable link created!</p>
            <p className="text-sm">Link copied to clipboard. Share it with your team.</p>
          </div>
        );
        
        // Refresh shared reports list
        const sharedRes = await reportAPI.getMyShared();
        setSharedReports(sharedRes.data || []);
        
        // Reset copied state after 3 seconds
        setTimeout(() => setCopiedId(null), 3000);
      }
    } catch (error) {
      console.error('Generate shareable link error:', error);
      toast.error('Failed to generate shareable link');
    } finally {
      setGenerating(null);
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleDeleteSharedReport = async (reportId) => {
    if (!window.confirm('Delete this shared report? The link will no longer work.')) return;
    
    try {
      await reportAPI.deleteShared(reportId);
      toast.success('Shared report deleted');
      setSharedReports(prev => prev.filter(r => r.id !== reportId));
    } catch (error) {
      toast.error('Failed to delete shared report');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'planning': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      'in-progress': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'completed': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'sold': 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return colors[status] || colors['planning'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">
          Generate shareable PDF reports for your investment projects
        </p>
      </div>

      {/* Shared Reports Section */}
      {sharedReports.length > 0 && (
        <Card className="border-border bg-card rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-primary" />
              Active Shared Links
            </CardTitle>
            <CardDescription>
              Links you've shared with your team
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sharedReports.map((report) => (
                <div 
                  key={report.id}
                  className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{report.project_name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        Expires: {formatDate(report.expires_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyLink(report.share_url)}
                      className="rounded-sm"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(report.share_url, '_blank')}
                      className="rounded-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSharedReport(report.id)}
                      className="rounded-sm text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Projects List */}
      <Card className="border-border bg-card rounded-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Generate Reports
          </CardTitle>
          <CardDescription>
            Select a project to generate a shareable PDF report
          </CardDescription>
        </CardHeader>
        <CardContent>
          {projects.length > 0 ? (
            <div className="space-y-4">
              {projects.map((project) => (
                <div 
                  key={project.id}
                  className="p-4 bg-secondary/30 rounded-sm border border-border hover:border-primary/30 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Project Info */}
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold truncate">{project.name}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {project.city}, {project.state}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-sm">
                          <Badge variant="outline" className={`rounded-sm ${getStatusColor(project.status)}`}>
                            {project.status}
                          </Badge>
                          <span className="text-muted-foreground flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            {formatCurrency(project.purchase_price)}
                          </span>
                          <span className="text-green-400 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            ARV: {formatCurrency(project.estimated_arv)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Link to={`/projects/${project.id}`}>
                        <Button variant="outline" size="sm" className="rounded-sm">
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                      </Link>
                      <Button 
                        onClick={() => handlePreviewReport(project)}
                        disabled={loadingPreview && previewProject?.id === project.id}
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                      >
                        {loadingPreview && previewProject?.id === project.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 mr-2" />
                            Preview
                          </>
                        )}
                      </Button>
                      <Button 
                        onClick={() => handleDownloadReport(project)}
                        disabled={downloading === project.id}
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                      >
                        {downloading === project.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </>
                        )}
                      </Button>
                      <Button 
                        onClick={() => handleGenerateShareableLink(project)}
                        disabled={generating === project.id}
                        size="sm"
                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                      >
                        {generating === project.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : copiedId === project.id ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Share2 className="w-4 h-4 mr-2" />
                            Share
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-semibold text-lg mb-2">No Projects Yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create a project to generate shareable reports
              </p>
              <Link to="/projects/new">
                <Button className="bg-primary text-primary-foreground rounded-sm">
                  Create First Project
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="border-border bg-card rounded-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg">How Shareable Reports Work</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-primary">1</span>
              </div>
              <h4 className="font-semibold mb-1">Click "Share Report"</h4>
              <p className="text-sm text-muted-foreground">
                Generate a PDF and get a shareable link instantly
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-primary">2</span>
              </div>
              <h4 className="font-semibold mb-1">Link Copied Automatically</h4>
              <p className="text-sm text-muted-foreground">
                The link is copied to your clipboard - ready to share
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-primary">3</span>
              </div>
              <h4 className="font-semibold mb-1">Team Views PDF</h4>
              <p className="text-sm text-muted-foreground">
                Anyone with the link can view the PDF (valid for 30 days)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Preview Modal */}
      <ReportPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        reportData={previewData}
        onDownload={() => handleDownloadReport(previewProject)}
        onShare={() => handleGenerateShareableLink(previewProject)}
        downloading={downloading === previewProject?.id}
        sharing={generating === previewProject?.id}
      />
    </div>
  );
};

export default ReportsPage;
